/**
 * Credit Status Codes & Configuration
 *
 * These status codes are returned in the response body (not HTTP status)
 * by 5 credit-consuming APIs when credit issues are detected.
 *
 * Priority order when multiple failures apply:
 *   1. 302 (Plan / Subscription)
 *   2. 303 (Missing credit record)
 *   3. 304 (Zero credits)
 *   4. 301 (Insufficient credits)
 */

// Credit-related status codes from backend
export const CREDIT_STATUS_CODES = {
  INSUFFICIENT: 301, // Not enough credits to perform the action
  NOT_IN_PLAN: 302, // Feature not in plan or no active subscription
  MISSING_RECORD: 303, // User credit record missing
  ZERO_CREDITS: 304, // Zero credits available
};

// Credit system event emitters
export const EMITTER_CREDIT_WARNING = "credit_warning";
export const EMITTER_LOW_CREDITS = "low_credits_warning";
export const EMITTER_CREDIT_UPDATE = "credit_update";
export const EMITTER_SDR_STATUS = "sdr_status_update";

// Low credit threshold - triggers a warning modal when credits fall below this
export const CREDIT_THRESHOLD = 100;

// All valid credit status codes as an array (for quick lookups)
export const ALL_CREDIT_STATUS_CODES = Object.values(CREDIT_STATUS_CODES);

// Modal type identifiers
export const CREDIT_MODAL_TYPES = {
  INSUFFICIENT: "INSUFFICIENT_CREDITS",
  NOT_IN_PLAN: "FEATURE_NOT_IN_PLAN",
  MISSING_RECORD: "MISSING_CREDIT_RECORD",
  ZERO_CREDITS: "ZERO_CREDITS",
  LOW_CREDITS: "LOW_CREDITS_WARNING",
};

// Map status codes to modal types
export const STATUS_CODE_TO_MODAL = {
  [CREDIT_STATUS_CODES.INSUFFICIENT]: CREDIT_MODAL_TYPES.INSUFFICIENT,
  [CREDIT_STATUS_CODES.NOT_IN_PLAN]: CREDIT_MODAL_TYPES.NOT_IN_PLAN,
  [CREDIT_STATUS_CODES.MISSING_RECORD]: CREDIT_MODAL_TYPES.MISSING_RECORD,
  [CREDIT_STATUS_CODES.ZERO_CREDITS]: CREDIT_MODAL_TYPES.ZERO_CREDITS,
};

// Modal metadata for each type
export const MODAL_CONFIG = {
  [CREDIT_MODAL_TYPES.INSUFFICIENT]: {
    heading: "Insufficient Credits",
    description:
      "You don't have enough credits to complete this action. Purchase additional credits or upgrade your plan to continue.",
    severity: "warning",
  },
  [CREDIT_MODAL_TYPES.NOT_IN_PLAN]: {
    heading: "Feature Not Available",
    description:
      "This feature is not included in your current plan or you don't have an active subscription. Upgrade your plan to unlock this feature.",
    severity: "error",
  },
  [CREDIT_MODAL_TYPES.MISSING_RECORD]: {
    heading: "Credit Record Missing",
    description:
      "We couldn't find your credit record. Please contact support to resolve this issue.",
    severity: "error",
  },
  [CREDIT_MODAL_TYPES.ZERO_CREDITS]: {
    heading: "No Credits Remaining",
    description:
      "You've used all your available credits. Purchase more credits to continue using this feature.",
    severity: "error",
  },
  [CREDIT_MODAL_TYPES.LOW_CREDITS]: {
    heading: "Credits Running Low",
    description:
      "You have fewer than 100 credits remaining. Consider purchasing more to avoid interruptions.",
    severity: "warning",
  },
};
